import { z } from 'zod';
import { RequestListRowSchema } from './aggregate.js';
import { RequestKindSchema } from './kind.js';
import { RequestStatus } from './status.js';

// WHY: `managerId` filter is a no-op for the manager role itself —
// managers always see only their own requests per Q8 of the founder
// discovery; the field is for higher-privilege actors (logist, admin)
// who can scope a list to a specific manager.
//
// INVARIANT: numerics use `z.coerce.number()` to match the convention
// established by `GetAuditQuerySchema` — Nest serializes HTTP query
// values as strings and the wire schema must parse them in their
// transport form. The status filter must accept three transport forms
// so it round-trips through any consumer:
//   1. an array when Nest yields it from repeated keys
//      (`?status=IN_WORK&status=PROCESSED`);
//   2. a scalar when a single status is supplied
//      (`?status=IN_WORK`);
//   3. a comma-separated string emitted by the existing scalar-only
//      `QueryValue` serializer in `apps/web/src/api/client.ts`
//      (`String(['IN_WORK','PROCESSED'])` → `'IN_WORK,PROCESSED'`).
const SingleStatusOrArraySchema = z.union([
  z.array(z.nativeEnum(RequestStatus)),
  z.nativeEnum(RequestStatus).transform((s) => [s]),
  z
    .string()
    .transform((s) =>
      s
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
    )
    .pipe(z.array(z.nativeEnum(RequestStatus)).min(1)),
]);

// WHY: `slaState` accepts the same three transport forms as `status`
// (array | scalar | comma-separated). Mirroring `SingleStatusOrArraySchema`
// keeps repeated-key, single-value, and comma-list URLs all valid so
// `serializeFiltersToUrl` can pick whichever form fits the page state
// without introducing a parallel parser.
const SlaStateValueSchema = z.enum(['hot', 'overdue']);
const SingleSlaStateOrArraySchema = z.union([
  z.array(SlaStateValueSchema),
  SlaStateValueSchema.transform((s) => [s]),
  z
    .string()
    .transform((s) =>
      s
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
    )
    .pipe(z.array(SlaStateValueSchema).min(1)),
]);

// WHY: `managerId` accepts the same three transport forms as `status` /
// `slaState` (array | scalar | comma-separated UUID). Legacy URLs like
// `?managerId=<uuid>` keep parsing; new multi-select web bundles emit
// repeated `?managerId=a&managerId=b`; direct callers can use a
// comma-list. The parsed output is always `string[]` so the backend
// predicate is uniformly `{ in: [...] }`. `.min(1)` after normalization
// rejects empty arrays at the boundary; `.max(100)` is a defensive cap
// against pathological `WHERE managerId IN (...)` lists from direct
// callers.
//
// INVARIANT: the array branch flat-splits every element on commas because
// `URLSearchParams.getAll('managerId')` returns the SAME array shape
// whether the URL uses repeated keys (`?managerId=a&managerId=b` → `['a','b']`)
// or a single comma list (`?managerId=a,b` → `['a,b']`). Keeping the
// split inside this schema is the single source of comma parsing — the
// web parser stays a thin aggregator and direct callers get identical
// semantics.
const SingleManagerIdOrArraySchema = z.union([
  z
    .array(z.string())
    .transform((arr) =>
      arr.flatMap((s) =>
        s
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean),
      ),
    )
    .pipe(z.array(z.string().uuid()).min(1).max(100)),
  z
    .string()
    .uuid()
    .transform((s) => [s]),
  z
    .string()
    .transform((s) =>
      s
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
    )
    .pipe(z.array(z.string().uuid()).min(1).max(100)),
]);

/**
 * @aiPurpose actor-scoped boolean flag for the `Unread` request-list
 *   chip (issue #583). The filter is personal to the JWT subject — the
 *   backend computes "unread for me" from `RequestChatReadState` joined
 *   on `actor.user.sub`, NOT from any client-supplied user id. There is
 *   intentionally no `unreadForUserId` field; a future authoring-team
 *   variant would need its own contract and a fresh permission gate.
 * @aiInvariant `z.coerce.boolean()` is deliberately NOT used because in
 *   Zod that coercion treats any non-empty string (including the literal
 *   `"false"`) as `true` — exactly the footgun that resolved-decision
 *   §7 of #583 rejects. This parser accepts a real `boolean` (programmatic
 *   callers, unit tests, future JSON-bodied calls) and the canonical
 *   query-string forms `"true"` / `"false"`. Anything else surfaces the
 *   standard `VALIDATION_ERROR` envelope at the boundary.
 * @aiSecurity the flag never widens visibility. The backend prefilter
 *   stays outside `buildVisibilityWhere`; the outer `where: { AND: [...] }`
 *   remains the security boundary, so a `true` value only narrows the
 *   visible row set, never expands it.
 */
const BooleanQueryFlagSchema = z.union([
  z.boolean(),
  z.enum(['true', 'false']).transform((v) => v === 'true'),
]);

export const RequestListQuerySchema = z
  .object({
    status: SingleStatusOrArraySchema.optional(),
    slaState: SingleSlaStateOrArraySchema.optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    // INVARIANT (#420): substring search; min(1) so the empty-string
    //   sentinel is rejected at the boundary (clients must omit the
    //   key, not send a blank value), max(200) for parity with
    //   `CarrierListQuerySchema.q` and to bound the LIKE workload.
    // INVARIANT (#431): no C0 control characters. The backend predicate
    //   maps `q` against a `chr(1)`-joined `searchHash` blob; without
    //   this gate, a client could send `q=%01` and match every row by
    //   colliding with the field separator. Production input is plain
    //   topbar text; this just makes the assumption explicit.
    q: z
      .string()
      .min(1)
      .max(200)
      // C0 only (U+0000..U+001F). DEL (U+007F) and any printable
      // character pass through; the only collision risk is chr(1),
      // which lives in C0 and is therefore rejected here.
      // eslint-disable-next-line no-control-regex -- intentional: rejecting C0 is the whole point.
      .regex(/^[^\u0000-\u001f]+$/, 'q must not contain control characters')
      .optional(),
    weightFrom: z.coerce.number().nonnegative().optional(),
    weightTo: z.coerce.number().nonnegative().optional(),
    volumeFrom: z.coerce.number().nonnegative().optional(),
    volumeTo: z.coerce.number().nonnegative().optional(),
    managerId: SingleManagerIdOrArraySchema.optional(),
    // INVARIANT (#583): actor-scoped unread filter. `true` narrows the
    //   result set to requests where the JWT subject has unread chat
    //   messages from a counterparty OR has manually marked the chat
    //   as unread. `false` is accepted at the wire for safe parsing
    //   but the backend treats it as if the key were absent (the
    //   serializer on the web side likewise omits the key when the
    //   chip is inactive). The inverse "only read" filter is NOT a
    //   feature of this contract.
    unread: BooleanQueryFlagSchema.optional(),
    // INVARIANT (#1054): scalar-only `kind` filter for the China request
    //   list. Single-select on the web side; the wire is intentionally
    //   not an array — `?kind=STANDARD,CONTAINER` and `?kind=` both fail
    //   the enum, surfacing the standard `VALIDATION_ERROR` envelope.
    //   Omit the key to fetch both kinds.
    kind: RequestKindSchema.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type RequestListQuery = z.infer<typeof RequestListQuerySchema>;

export const RequestListResponseSchema = z
  .object({
    // WHY (#1662): list rows use `RequestListRowSchema` (chat/audit
    //   optional) so the always-slim list response parses. The DETAIL
    //   `RequestSchema` keeps requiring both keys.
    items: z.array(RequestListRowSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type RequestListResponse = z.infer<typeof RequestListResponseSchema>;

/**
 * @aiPurpose minimal manager reference used by the request-list manager
 *   picker (issue #421). Returned by `GET /api/requests/manager-options`,
 *   one entry per distinct `Request.managerId` visible to the caller
 *   under the request-domain visibility predicate.
 * @aiInvariant `id` is the manager's user id (UUID) — kept tight as
 *   `z.string().uuid()` so a backend/version-skew/mock drift cannot
 *   surface a non-UUID option that would round-trip into the picker
 *   then fail `RequestListQuerySchema.managerId` (also `.uuid()`) on
 *   the follow-up `/requests?managerId=...` call. `name` is the
 *   manager's display name. The shape is intentionally minimal —
 *   only what the picker `<option value=id>{name}</option>` renders.
 *   Any future field must justify why it cannot be derived from the
 *   row set the caller already has via `GET /requests`.
 */
export const RequestManagerOptionSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
  })
  .strict();
export type RequestManagerOption = z.infer<typeof RequestManagerOptionSchema>;

/**
 * @aiPurpose response wire shape for `GET /api/requests/manager-options`.
 * @aiInvariant flat array (no envelope) — matches `useUsersList`'s
 *   shape so the picker swap on the web side stays a one-line change.
 */
export const RequestManagerOptionsResponseSchema = z.array(RequestManagerOptionSchema);
export type RequestManagerOptionsResponse = z.infer<typeof RequestManagerOptionsResponseSchema>;
